import * as React from 'react';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
  PLACEMENT_STATUS_ACTIVE,
  PLACEMENT_STATUS_CANCELLED,
} from '@careerstart/wae-common/src/main/constants/constants';
import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Box } from '@mui/material';

import PaginatedListWrapper from '../../../../../components/PagedFilterList';
import {
  selectIsLoadingWorkerPlacements,
  selectWorkerPlacements,
} from '../../../../../store/selectors/userListSelector';
import { FILTERS } from '../../../UsersConstants';
import { getWorkerPlacements } from '../../../usersListReducer';

import RowCards from './RowCards';
import WorkerPlacementsFilters from './WorkerPlacementsFilters';

const LIMIT = 5;
const ACTIVE_STATUS_FILTER = {
  field: 'state.status',
  operation: 'equals',
  value: PLACEMENT_STATUS_ACTIVE,
};
const ACTIVE_AND_CANCELLED_STATUS_FILTER = {
  field: 'state.status',
  operation: 'includedIn',
  value: [PLACEMENT_STATUS_ACTIVE, PLACEMENT_STATUS_CANCELLED],
};

const WorkerPlacements = ({ user }) => {
  const dispatch = useDispatch();

  const isLoading = useSelector(selectIsLoadingWorkerPlacements);
  const workerPlacements = useSelector(selectWorkerPlacements);
  const [showCancellations, setShowCancellations] = useState(false);

  const INITIAL_FILTERS = {
    filters: [
      FILTERS.UPCOMING,
      {
        value: user.id,
        field: 'candidate',
        operation: 'equalsID',
      },
      ACTIVE_STATUS_FILTER,
    ],
    page: 0,
    limit: LIMIT,
    sortBy: [{ field: 'start', descending: false }],
  };
  const [filterAndSortData, setFilterAndSortData] = useState(INITIAL_FILTERS);

  const handleOnFilterChange = (e) => {
    const newFilter = e.target.checked ? FILTERS.UPCOMING : FILTERS.COMPLETED;

    const filters = [
      newFilter,
      {
        value: user?.id,
        field: 'candidate',
        operation: 'equalsID',
      },
    ];

    filters.push(showCancellations ? ACTIVE_AND_CANCELLED_STATUS_FILTER : ACTIVE_STATUS_FILTER);

    setFilterAndSortData((prevFilters) => ({
      filters,
      page: INITIAL_FILTERS.page,
      limit: prevFilters.limit,
      sortBy: [{ field: 'start', descending: newFilter === FILTERS.COMPLETED }],
    }));
  };

  const handleShowCancellationsChange = (e) => {
    const { checked } = e.target;
    setShowCancellations(checked);

    setFilterAndSortData((prev) => ({
      ...prev,
      page: 0,
      filters: [
        ...prev.filters.filter((f) => f.field !== 'state.status'),
        checked ? ACTIVE_AND_CANCELLED_STATUS_FILTER : ACTIVE_STATUS_FILTER,
      ],
    }));
  };

  useEffect(() => {
    dispatch(getWorkerPlacements(filterAndSortData));
  }, [dispatch, filterAndSortData]);

  const handleOnPageChange = (newPage) => {
    setFilterAndSortData((prevState) => ({
      ...prevState,
      page: newPage,
    }));
  };

  const title = LanguageConverter('userDetail.workerPlacements.title');

  const isChecked = !!filterAndSortData.filters.find(
    (filter) =>
      filter.field === FILTERS.UPCOMING.field && filter.operation === FILTERS.UPCOMING.operation
  );

  return (
    <Box>
      <PaginatedListWrapper
        isLoading={isLoading}
        content={<RowCards placements={workerPlacements?.documents} />}
        limit={LIMIT}
        filters={
          <WorkerPlacementsFilters
            isChecked={isChecked}
            isLoading={isLoading}
            onChange={handleOnFilterChange}
            showCancellations={showCancellations}
            onShowCancellationsChange={handleShowCancellationsChange}
          />
        }
        title={title}
        currentPageNum={filterAndSortData.page}
        totalListCount={workerPlacements?.count}
        handleOnPageChange={handleOnPageChange}
      />
    </Box>
  );
};
WorkerPlacements.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
};

export default WorkerPlacements;
